import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { ApolloQueryResult } from "@apollo/client";
import { useHistory } from "react-router";

import { PanelWidth } from "../../../../components/panel/Panel";
import {
  DagCommunityViewQuery,
  Exact,
  PaymentEntityType,
  PaymentMethod,
  usePaymentMethodsViewQuery,
  useWithdrawTokensMutation,
  ViewerQuery,
} from "../../../../graphql/schema";
import { Button } from "../../../../components/button/Button";
import { Color } from "../../../../services/buttonLinkConstants";
import { Modal, ModalKind } from "../../../../components/modal/Modal";
import { showGraphqlValidationErrors } from "../../../../services/showGraphqlValidationErrors";
import Field from "../../../../components/field/Field";
import { DefaultCurrencyCode, Routes } from "../../../../services/constants";
import { useHandelPayment } from "../../../../hooks/useHandlePayment";
import { TopUpModal } from "../../../../components/top-up-modal/TopUpModal";
import { UserDagTransactionList } from "./transaction-list/TransactionList";

import styles from "./dag-community-view.module.scss";

export interface DagCommunityDepositProps {
  me: ViewerQuery | undefined;
  totalDagsQuery: DagCommunityViewQuery | undefined;
  meRefetch: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<ViewerQuery>>;
  className?: string;
}

interface DepositDagsFormInfo {
  amount: string;
}
interface WithdrawDagsFormInfo {
  wallet: string;
  amount: string;
}

const depositDagsValidationSchema: yup.SchemaOf<DepositDagsFormInfo> = yup.object().shape({
  amount: yup.string().required("Amount is required"),
});

function useDepositDagsForm() {
  const { register, handleSubmit, errors, reset, setError } = useForm<DepositDagsFormInfo>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(depositDagsValidationSchema),
  });

  return {
    register,
    handleSubmit,
    errors,
    reset,
    setError,
  };
}

const withdrawDagsValidationSchema: yup.SchemaOf<WithdrawDagsFormInfo> = yup.object().shape({
  wallet: yup.string().required("Amount is required"),
  amount: yup.string().required("Amount is required"),
});

function useWithdrawDagsForm() {
  const { register, handleSubmit, errors, reset, setError } = useForm<WithdrawDagsFormInfo>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(withdrawDagsValidationSchema),
  });

  return {
    register,
    handleSubmit,
    errors,
    reset,
    setError,
  };
}

export const DagCommunityDeposit: FC<DagCommunityDepositProps> = () => {
  const { t } = useTranslation();
  const query = usePaymentMethodsViewQuery();
  const userId = query.data?.viewer?.id ?? null;
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isDepositBEP20ModalOpen, setIsDepositBEP20ModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDepositLBankModalOpen, setIsDepositLBankModalOpen] = useState(false);
  // const [paymentResId, setPaymentResId] = useState<string | null>(null);
  const { push } = useHistory();

  const handleDagDeposit = useHandelPayment({
    onSuccess: () => push(`${Routes.DAG_COMMUNITY}`),
    onError: () => toast.error(t("Something went wrong")),
    onUnknownResponse: () => console.warn("payment status unknwon"),
    returnUrl: `${Routes.DAG_COMMUNITY}`,
  });

  const [handleDagWithdraw] = useWithdrawTokensMutation();

  const {
    register: depositDags,
    handleSubmit: handleDagDepositSubmit,
    errors: depositDagsInputErrors,
  } = useDepositDagsForm();

  const handleDepositDagsFormSubmit = handleDagDepositSubmit(async ({ amount }) => {
    try {
      await handleDagDeposit({
        errorMessage: t("Something went wrong"),
        entityId: undefined,
        totalPrice: amount,
        paymentVariables: {
          entityType: PaymentEntityType.DEPOSIT,
          currencyCode: "DAG",
        },
        selectedPaymentSource: {
          id: null,
          paymentMethod: PaymentMethod.DAGPAY,
        },
      });

      // setPaymentResId(paymentRes.data?.payments.create.id ?? null);
    } catch (e) {
      if (!showGraphqlValidationErrors(t, e) && e instanceof Error) {
        toast.error(e.message, { autoClose: false });
      }
    }
  });

  const {
    register: depositBEP20Dags,
    handleSubmit: handleBEP20DagDepositSubmit,
    errors: depositBEP20DagsInputErrors,
  } = useDepositDagsForm();

  const handleDepositBEP20DagsFormSubmit = handleBEP20DagDepositSubmit(async ({ amount }) => {
    try {
      await handleDagDeposit({
        errorMessage: t("Something went wrong"),
        entityId: undefined,
        totalPrice: amount,
        paymentVariables: {
          entityType: PaymentEntityType.DEPOSIT,
          currencyCode: "DAG",
        },
        selectedPaymentSource: {
          id: null,
          paymentMethod: PaymentMethod.LEGEPAY_BEP20_DAGS,
        },
      });

      // setPaymentResId(paymentRes.data?.payments.create.id ?? null);
    } catch (e) {
      if (!showGraphqlValidationErrors(t, e) && e instanceof Error) {
        toast.error(e.message, { autoClose: false });
      }
    }
  });

  const {
    register: withdrawDags,
    handleSubmit: handleDagWithdrawSubmit,
    errors: withdrawDagsInputErrors,
    reset: resetWithdrawDagsForm,
  } = useWithdrawDagsForm();

  const handleWithdrawDagsFormSubmit = handleDagWithdrawSubmit(async ({ amount, wallet }) => {
    try {
      setIsSubmitting(true);
      const result = await handleDagWithdraw({
        variables: { amount, wallet },
      });

      if (result) {
        resetWithdrawDagsForm({
          amount: "",
          wallet: "",
        });
        toast.success(t("Withdraw was successfully submitted!"));
        setIsSubmitting(false);
      }
    } catch (e) {
      setIsSubmitting(false);
      if (!showGraphqlValidationErrors(t, e) && e instanceof Error) {
        toast.error(e.message, { autoClose: false });
      }
    }
  });

  return (
    <>
      <div className={styles["grid-container"]} style={{ paddingBottom: "50px" }}>
        <div className={styles["action"]}>
          <div className={styles["action-buttons"]}>
            {/* {![33, 1].includes(userId ?? 0) && (
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                onClick={() => setIsDepositModalOpen(true)}
                color={Color.DARK_BLUE}
                fontSize={14}
                style={{
                  width: 182,
                }}
              >
                <span className={styles["link-label"]}>{t("Deposit DAGS")}</span>
              </Button>
            )} */}

            <Button
              className={styles["access-product-link"]}
              borderRadius="MEDIUM"
              onClick={() => setIsDepositBEP20ModalOpen(true)}
              color={Color.DARK_BLUE}
              fontSize={14}
              style={{
                width: 182,
              }}
            >
              <span className={styles["link-label"]}>{t("Deposit DAGS")}</span>
            </Button>
            {/* <Button
              className={styles["access-product-link"]}
              borderRadius="MEDIUM"
              onClick={() => setIsDepositLBankModalOpen(true)}
              color={Color.DARK_BLUE}
              fontSize={14}
              style={{
                width: 182,
              }}
            >
              <span className={styles["link-label"]}>{t("Deposit DAGS from exchange")}</span>
            </Button> */}
          </div>
          <div className={styles["separator-action"]}>
            <div className={styles["separator-line"]} />
          </div>
          <div className={styles["action-right"]}>
            <Button
              className={styles["access-product-link"]}
              borderRadius="MEDIUM"
              onClick={() => setIsWithdrawModalOpen(true)}
              color={Color.DARK_BLUE}
              fontSize={14}
              style={{
                width: 182,
              }}
            >
              <span className={styles["link-label"]}>{t("Withdraw DAGS")}</span>
            </Button>
          </div>
        </div>
        {/* <div className={styles["separator"]}>
          <div className={styles["separator-line"]} />
        </div>
        <div className={styles["action-right"]}>
          <Button
            className={styles["access-product-link"]}
            borderRadius="MEDIUM"
            disabled
            color={Color.DARK_BLUE}
            fontSize={14}
          >
            <span className={styles["link-label"]}>{t("Withdraw DAGS")}</span>
          </Button>
          <p>
            <span className={styles["action-text"]}>Functionality in progress..</span>
          </p>
        </div> */}
      </div>
      <UserDagTransactionList />
      <Modal
        isOpen={isDepositModalOpen}
        close={() => {
          setIsDepositModalOpen(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("DAGS Deposit"),
          caption: t("Deposit your DAGS"),
          label: t("DAGS Deposit"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center", marginTop: "30px", marginBottom: "30px" }}>{t("Deposit")}</h2>
          <p style={{ marginBottom: "20px" }}>Enter the amount of DAGS you wish to deposit.</p>
          <form onSubmit={handleDepositDagsFormSubmit}>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("Deposit Amount")}
                name="amount"
                gutter={20}
                error={depositDagsInputErrors.amount}
                internalRef={depositDags}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
                width={200}
              >
                <span className={styles["link-label"]}>{t("Deposit DAGS")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isDepositBEP20ModalOpen}
        close={() => {
          setIsDepositBEP20ModalOpen(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("DAGS Deposit"),
          caption: t("Deposit your DAGS"),
          label: t("DAGS Deposit"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center", marginTop: "30px", marginBottom: "30px" }}>{t("Deposit")}</h2>
          <p style={{ marginBottom: "20px" }}>Enter the amount of DAGS (BSC, BEP-20) you wish to deposit.</p>
          <form onSubmit={handleDepositBEP20DagsFormSubmit}>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("Deposit Amount")}
                name="amount"
                gutter={20}
                error={depositBEP20DagsInputErrors.amount}
                internalRef={depositBEP20Dags}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
                width={200}
              >
                <span className={styles["link-label"]}>{t("Deposit DAGS")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isWithdrawModalOpen}
        close={() => {
          setIsWithdrawModalOpen(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("DAGS Withdrawal"),
          caption: t("Withdraw your DAGS"),
          label: t("DAGS Withdrawal"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center", marginTop: "30px", marginBottom: "30px" }}>{t("Withdrawal")}</h2>
          <p style={{ marginBottom: "20px" }}>Enter the amount of DAGS you wish to withdraw.</p>
          <form onSubmit={handleWithdrawDagsFormSubmit}>
            <div style={{ margin: "20px 0" }}>
              <p>Withdrawal address (bep20, BNB chain)</p>
              <Field
                type="text"
                className={styles.field}
                label={t("Withdraw wallet")}
                name="wallet"
                gutter={20}
                error={withdrawDagsInputErrors.wallet}
                internalRef={withdrawDags}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
              <Field
                type="text"
                className={styles.field}
                label={t("Withdraw Amount")}
                name="amount"
                gutter={20}
                error={withdrawDagsInputErrors.amount}
                internalRef={withdrawDags}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
              <p>Withdrawal fee for BNB chain is 350 DAGS</p>
            </div>
            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                disabled={isSubmitting}
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
                width={200}
              >
                <span className={styles["link-label"]}>{t("Withdraw DAGS")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal
        isOpen={isDepositLBankModalOpen}
        close={() => {
          setIsDepositLBankModalOpen(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("Top up your DAGS balance from exchange"),
          caption: t("Top up your DAGS balance from exchange"),
          label: t("Top up your DAGS balance from exchange"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h2 style={{ textAlign: "center", marginTop: "30px", marginBottom: "30px" }}>
            {t("Top up your DAGS balance from exchange")}
          </h2>
          <div style={{ margin: "20px 0" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="400" height="90" viewBox="0 0 80 16" fill="#FFDB00">
              <g clip-path="url(#clip0_83_11539)">
                <path
                  d="M59.9834 2.64641C59.9061 2.95938 59.8602 3.29246 59.8516 3.64276C59.8144 5.28517 59.843 6.99362 59.8401 8.63889C59.8401 8.90018 59.8086 9.16148 59.78 9.65822C59.4046 9.21603 54.9779 3.75762 53.0009 1.30549C52.9665 1.26242 52.9321 1.22509 52.8948 1.1849L52.0181 0.102401H50.8491C50.4852 0.0593305 50.1099 0.0650732 49.7259 0.102401H49.2331V15.8976C50.9866 16.0986 52.5424 14.4792 52.5854 12.4147C52.6141 10.9934 52.5911 9.6697 52.5911 8.24839V6.53994C52.5911 6.53994 56.0007 10.511 58.2843 13.2417L60.4218 15.8833H61.7742C62.0378 15.9063 62.3071 15.9034 62.585 15.8833H63.2068V0.0736872C61.6252 0.00764639 60.3759 1.06143 59.9777 2.64641H59.9834Z"
                  fill="#FFDB00"
                ></path>
                <path
                  d="M14.0167 15.8718H0V0.0823218C1.93403 -0.0669879 3.47551 1.47779 3.50417 3.62268C3.53282 5.74747 3.5099 7.87514 3.5099 9.99993V12.3171H4.49553C7.11148 12.3171 8.04268 12.2999 10.6586 12.3228C12.5554 12.3401 14.0453 13.8992 14.0138 15.8689L14.0167 15.8718Z"
                  fill="#FFDB00"
                ></path>
                <path
                  d="M78.9742 11.4212L74.0489 7.31518L78.5989 4.52998C79.9742 3.68867 80.4097 1.88547 79.5702 0.507229L79.2751 0.0219727L69.413 6.05753V2.97084C69.413 1.35715 68.1036 0.0449434 66.4933 0.0449434H65.926V15.8775H66.4933C68.1036 15.8775 69.413 14.5653 69.413 12.9516V10.1521L70.9029 9.24184L78.9341 15.978L79.3008 15.5473C80.3467 14.3184 80.2034 12.4721 78.9771 11.4241L78.9742 11.4212Z"
                  fill="#FFDB00"
                ></path>
                <path
                  d="M47.3421 15.1625C45.6029 10.6143 43.8666 6.06612 42.1045 1.52941L41.5572 0.073637H37.5774L37.0703 1.42317C36.2021 3.72311 35.3168 6.01731 34.44 8.31438C33.5174 10.7263 32.5977 13.1382 31.6779 15.5444C33.1535 16.3283 34.9701 15.6937 35.7895 14.0944C36.0245 13.635 36.1047 13.5029 36.1047 13.5029H43.0615C43.0615 13.5029 43.1331 13.635 43.4196 14.169C44.2391 15.6995 45.8894 16.2709 47.4567 15.5904C47.4137 15.4209 47.3908 15.286 47.345 15.1654L47.3421 15.1625ZM37.4399 9.92521C38.1476 8.06745 38.8209 6.28722 39.5773 4.30599C40.3366 6.29296 41.0214 8.07319 41.7291 9.92521H37.4399Z"
                  fill="#FFDB00"
                ></path>
                <path
                  d="M29.3886 9.15287C28.9358 8.46088 28.9559 7.61096 29.3914 6.94194C29.8384 6.25856 30.0963 5.44597 30.1049 4.57309C30.1221 2.11522 28.0591 0.142609 25.6065 0.142609H17.7959C16.6842 0.142609 15.7845 1.04421 15.7845 2.15829V13.9566C15.7845 15.0707 16.6842 15.9723 17.7959 15.9723H25.7182V15.9608C28.1422 15.9608 30.1077 13.9911 30.1077 11.5619C30.1077 10.6718 29.8441 9.84487 29.3914 9.15574L29.3886 9.15287ZM24.5549 13.882C23.5435 14.7348 22.0249 14.6515 21.1854 13.6982C20.3631 12.7621 20.3688 11.4672 21.1596 10.5081C21.5292 10.0602 21.9103 9.58932 22.1367 9.06386C22.5063 8.21394 22.3315 7.12571 21.7671 6.3849C21.5264 6.07193 21.2542 5.78479 21.0164 5.47181C20.3373 4.57883 20.3717 3.32118 21.0937 2.49998C21.8587 1.62709 23.0822 1.39451 24.0678 1.94581C24.2684 2.05779 24.446 2.187 24.6065 2.33344H24.6036C24.9073 2.6177 25.1308 2.96801 25.2426 3.40158C25.3887 3.95575 25.3285 4.55299 25.085 5.06983C24.9933 5.26508 24.8758 5.44884 24.7383 5.61538C24.4862 5.91687 24.2226 6.21549 23.9991 6.53708C23.3859 7.43581 23.4375 8.87435 24.108 9.73575C24.3486 10.0459 24.6151 10.3416 24.8586 10.6488C25.6265 11.6165 25.4833 13.101 24.5521 13.8848L24.5549 13.882Z"
                  fill="#FFDB00"
                ></path>
              </g>
              <defs>
                <clipPath id="clip0_83_11539">
                  <rect width="180" height="16" fill="#FFDB00"></rect>
                </clipPath>
              </defs>
            </svg>
          </div>
          <p style={{ marginBottom: "20px" }}>Make Internal Transfer or Internal Withdraw to UID:</p>
          <p style={{ marginBottom: "20px" }}>
            <strong>LBA1G46317</strong>
          </p>
          <p style={{ marginBottom: "20px" }}>
            Send screenshot of the transfer and your username to <strong>payments@sfsuite.com</strong>
          </p>
          <p style={{ marginBottom: "20px" }}>
            <strong>Your balance will be credited within 24h</strong>
          </p>
        </div>
      </Modal>
    </>
  );
};
